import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Input, Checkbox, Spin } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from '../../../../components/ui';
import { createRole, getRole, updateRole, roleKeys } from '../../../../api/role.api';
import { toast, handleMutationError } from '../../../../lib/notification';
import { usePermissionTree } from '../../../../hooks/usePermissionTree';
import type { CreateRolePayload } from '../../../../types/role.types';
import PermissionTree from './PermissionTree';

type FormMode = 'create' | 'view' | 'edit';

function useRoleFormMode(): { mode: FormMode; roleId: string | null } {
	const { pathname } = useLocation();

	return useMemo(() => {
		if (pathname.endsWith('/create')) {
			return { mode: 'create' as const, roleId: null };
		}

		// /iam/roles/:id/edit or /iam/roles/:id
		const segments = pathname.split('/');
		const last = segments[segments.length - 1]!;
		if (last === 'edit') {
			return { mode: 'edit' as const, roleId: segments[segments.length - 2] ?? null };
		}

		// /iam/roles/:id
		return { mode: 'view' as const, roleId: last };
	}, [pathname]);
}

interface RoleFormViewProps {
	editPermission?: string;
}

export default function RoleFormView({ editPermission = 'iam:role:update' }: RoleFormViewProps = {}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { mode, roleId } = useRoleFormMode();
	const [form] = Form.useForm();
	const [isActive, setIsActive] = useState(true);
	const isReadOnly = mode === 'view';

	const {
		selectedPermissions,
		selectedArray,
		togglePermission,
		toggleGroup,
		getGroupState,
		setPermissions,
	} = usePermissionTree();

	// Fetch role detail for view/edit modes
	const { data: roleDetail, isLoading: isRoleLoading } = useQuery({
		queryKey: roleKeys.detail(roleId!),
		queryFn: () => getRole(roleId!),
		enabled: !!roleId,
	});

	// Populate form when role data loads
	useEffect(() => {
		if (roleDetail) {
			form.setFieldsValue({ title: roleDetail.title });
			setIsActive(roleDetail.isActive);
			setPermissions(roleDetail.permissions);
		}
	}, [roleDetail, form, setPermissions]);

	// Create mutation
	const createMutation = useMutation({
		mutationFn: createRole,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
			toast.success('Role created successfully');
			navigate('/iam/roles');
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to create role');
		},
	});

	// Update mutation
	const updateMutation = useMutation({
		mutationFn: (payload: CreateRolePayload) => updateRole(roleId!, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
			queryClient.invalidateQueries({ queryKey: roleKeys.detail(roleId!) });
			toast.success('Role updated successfully');
			navigate(`/iam/roles/${roleId}`);
		},
		onError: (error: unknown) => {
			handleMutationError(error, 'Failed to update role');
		},
	});

	const handleSubmit = (values: { title: string }) => {
		const payload: CreateRolePayload = {
			title: values.title,
			isActive,
			status: isActive ? 'ACTIVE' : 'INACTIVE',
			permissions: selectedArray,
		};

		if (mode === 'edit') {
			updateMutation.mutate(payload);
		} else {
			createMutation.mutate(payload);
		}
	};

	const isSaving = createMutation.isPending || updateMutation.isPending;

	const pageTitle = mode === 'create' ? 'Create Role' : mode === 'edit' ? 'Edit Role' : 'Role Details';

	if (roleId && isRoleLoading) {
		return (
			<div className="flex justify-center py-24">
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<Button
					variant="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate('/iam/roles')}
					size="small"
				>
					Back to Roles
				</Button>

				{isReadOnly && (
					<Button
						variant="primary"
						icon={<EditOutlined />}
						permission={editPermission}
						onClick={() => navigate(`/iam/roles/${roleId}/edit`)}
					>
						Edit
					</Button>
				)}
			</div>

			<h2 className="text-lg font-semibold text-gray-900 mb-6">{pageTitle}</h2>

			<Form form={form} layout="vertical" onFinish={handleSubmit}>
				<div className="flex items-center justify-between gap-6">
					<Form.Item
						name="title"
						label="Role Name"
						rules={[
							{ required: true, message: 'Please enter a role name' },
							{ max: 200, message: 'Role name must not exceed 200 characters' },
						]}
						className="mb-0 w-[350px]"
					>
						<Input placeholder="Role Name" size="large" disabled={isReadOnly} />
					</Form.Item>

					<div className="flex items-center gap-2">
						<Checkbox
							checked={isActive}
							disabled={isReadOnly}
							onChange={(e) => setIsActive(e.target.checked)}
						>
							Active
						</Checkbox>
					</div>
				</div>

				<div className="mb-6">
					<h2 className="text-lg font-semibold text-gray-800 mb-4">Modules & Permissions</h2>

					<PermissionTree
						selectedPermissions={selectedPermissions}
						onTogglePermission={togglePermission}
						onToggleGroup={toggleGroup}
						getGroupState={getGroupState}
						disabled={isReadOnly}
					/>
				</div>

				{!isReadOnly && (
					<div className="flex justify-end gap-3 pt-4">
						<Button variant="secondary" onClick={() => navigate('/iam/roles')}>
							Cancel
						</Button>
						<Button variant="primary" htmlType="submit" loading={isSaving}>
							{mode === 'edit' ? 'Update Role' : 'Create Role'}
						</Button>
					</div>
				)}
			</Form>
		</div>
	);
}
